<template>
  <div
    class="rotating-grid"
    :style="{
      '--rg-cols': cols,
      '--rg-cols-mobile': mobileCols,
      '--rg-gap': gap
    }"
    @mouseenter="pauseRotation"
    @mouseleave="resumeRotation"
    @touchstart.passive="onTouchStart"
    @touchend="onTouchEnd"
    @touchcancel="onTouchCancel"
    @click.capture="onClickCapture"
  >
    <div
      class="rotating-grid__group"
      :class="{ 'is-swapping': swapping, 'is-paused': paused }"
    >
      <slot :items="visibleItems" />
    </div>

    <!-- Indicadores: el activo es un rectángulo con barra de tiempo; el resto cuadraditos -->
    <div
      v-if="canRotate"
      class="gallery-dots"
      role="group"
      aria-label="Cambiar de grupo"
      :style="{ '--dot-duration': interval + 'ms' }"
    >
      <button
        v-for="(group, index) in groups"
        :key="index"
        type="button"
        class="gallery-dot"
        :class="{ 'is-active': index === activeGroup }"
        :aria-label="`Ver el grupo ${index + 1} de ${groups.length}`"
        :aria-current="index === activeGroup ? 'true' : null"
        @click="goToGroup(index)"
      >
        <span
          v-if="index === activeGroup"
          :key="cycle"
          class="gallery-dot-fill"
          :class="{ 'is-paused': paused }"
          aria-hidden="true"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, defineProps, onUnmounted } from 'vue'; // eslint-disable-line no-unused-vars

/*
 * Grilla que rota por grupos, con los mismos efectos que el carrusel de la
 * galería: las tarjetas salen con opacidad + scale y entran las del grupo
 * siguiente, más los indicadores con barra de tiempo.
 */
const props = defineProps({
  items: {
    type: Array,
    default: () => []
  },
  size: {
    type: Number,
    default: 4 // ítems por grupo
  },
  interval: {
    type: Number,
    default: 4000 // ms que cada grupo queda activo
  },
  cols: {
    type: Number,
    default: 4 // columnas en desktop
  },
  mobileCols: {
    type: Number,
    default: 2 // columnas en mobile
  },
  gap: {
    type: String,
    default: '1.5rem'
  }
});

const SWAP_MS = 320; // debe coincidir con la salida de las tarjetas en CSS

const groups = computed(() => {
  const list = props.items || [];
  const out = [];
  for (let index = 0; index < list.length; index += props.size) {
    out.push(list.slice(index, index + props.size));
  }
  return out;
});

const activeGroup = ref(0);
const visibleItems = computed(() => groups.value[activeGroup.value] || []);

const swapping = ref(false);
const paused = ref(false);
const cycle = ref(0); // cambia para reiniciar la barra de tiempo del indicador
let rotateTimer = null;
let swapTimer = null;
let touchStart = null;
let lastSwipeAt = 0;

const prefersReducedMotion = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const canRotate = computed(() => groups.value.length > 1);

function stopRotation() {
  if (rotateTimer) {
    window.clearInterval(rotateTimer);
    rotateTimer = null;
  }
}

function startRotation() {
  stopRotation();

  if (!canRotate.value || paused.value || prefersReducedMotion) {
    return;
  }

  rotateTimer = window.setInterval(() => {
    goToGroup((activeGroup.value + 1) % groups.value.length);
  }, props.interval);
}

function goToGroup(index) {
  if (index === activeGroup.value || swapping.value || !groups.value.length) {
    return;
  }

  // Salida: las tarjetas se van con opacidad + scale
  stopRotation();
  swapping.value = true;

  if (swapTimer) {
    window.clearTimeout(swapTimer);
  }

  swapTimer = window.setTimeout(() => {
    swapTimer = null;
    activeGroup.value = index;
    cycle.value += 1;

    nextTick(() => {
      swapping.value = false;
      startRotation();
    });
  }, SWAP_MS);
}

function onTouchStart(event) {
  if (!canRotate.value || !window.matchMedia('(max-width: 800px)').matches || event.touches.length !== 1) {
    touchStart = null;
    return;
  }

  touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
}

function onTouchEnd(event) {
  if (!touchStart || event.changedTouches.length !== 1) return;

  const dx = event.changedTouches[0].clientX - touchStart.x;
  const dy = event.changedTouches[0].clientY - touchStart.y;
  touchStart = null;

  if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;

  lastSwipeAt = Date.now();
  if (swapping.value) return;
  goToGroup((activeGroup.value + (dx < 0 ? 1 : groups.value.length - 1)) % groups.value.length);
}

function onTouchCancel() {
  touchStart = null;
}

function onClickCapture(event) {
  if (Date.now() - lastSwipeAt < 450) {
    event.preventDefault();
    event.stopPropagation();
    lastSwipeAt = 0;
  }
}

function pauseRotation() {
  if (!window.matchMedia('(hover: hover)').matches) return;
  paused.value = true;
  stopRotation();
}

function resumeRotation() {
  if (!window.matchMedia('(hover: hover)').matches) return;
  if (!canRotate.value) {
    return;
  }

  paused.value = false;
  cycle.value += 1; // reinicia la barra de tiempo junto con el interval
  startRotation();
}

// Con menos de dos grupos no hay rotación; si cambia la lista, se reacomoda
watch(canRotate, (value) => {
  if (value) {
    startRotation();
  } else {
    stopRotation();
  }
});

watch(groups, (list) => {
  if (activeGroup.value >= list.length) {
    activeGroup.value = 0;
  }

  startRotation();
});

onUnmounted(() => {
  stopRotation();

  if (swapTimer) {
    window.clearTimeout(swapTimer);
    swapTimer = null;
  }
});
</script>

<style scoped>
.rotating-grid {
  position: relative;
}

.rotating-grid__group {
  display: grid;
  grid-template-columns: repeat(var(--rg-cols, 4), minmax(0, 1fr));
  gap: var(--rg-gap, 1.5rem);
}

/* Salida y entrada: opacidad + scale, escalonado por tarjeta */
.rotating-grid__group > * {
  transition: opacity 0.32s ease, transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.rotating-grid__group > *:nth-child(1) { transition-delay: 0ms; }
.rotating-grid__group > *:nth-child(2) { transition-delay: 45ms; }
.rotating-grid__group > *:nth-child(3) { transition-delay: 90ms; }
.rotating-grid__group > *:nth-child(4) { transition-delay: 135ms; }
.rotating-grid__group > *:nth-child(5) { transition-delay: 180ms; }
.rotating-grid__group > *:nth-child(6) { transition-delay: 225ms; }

.rotating-grid__group.is-swapping {
  pointer-events: none;
}

.rotating-grid__group.is-swapping > * {
  opacity: 0;
  transform: scale(0.955);
  transition-duration: 0.22s;
}

.rotating-grid__group.is-swapping > *:nth-child(1) { transition-delay: 80ms; }
.rotating-grid__group.is-swapping > *:nth-child(2) { transition-delay: 60ms; }
.rotating-grid__group.is-swapping > *:nth-child(3) { transition-delay: 40ms; }
.rotating-grid__group.is-swapping > *:nth-child(4) { transition-delay: 20ms; }
.rotating-grid__group.is-swapping > *:nth-child(5) { transition-delay: 0ms; }
.rotating-grid__group.is-swapping > *:nth-child(6) { transition-delay: 0ms; }

/* =========================================================
   INDICADORES (idénticos al carrusel de la galería)
   ========================================================= */

.gallery-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.75rem;
}

.gallery-dot {
  position: relative;
  width: 4px;
  height: 4px;
  padding: 0;
  border: 0;
  background: rgba(255, 255, 255, 0.28);
  cursor: pointer;
  transition: width 0.4s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.3s ease;
}

.gallery-dot:hover {
  background: rgba(255, 255, 255, 0.55);
}

.gallery-dot:focus-visible {
  outline: 1px solid #fff;
  outline-offset: 4px;
}

.gallery-dot.is-active {
  width: 42px;
  background: rgba(255, 255, 255, 0.16);
}

.gallery-dot-fill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 0;
  background: #ffffff;
  animation: gallery-dot-fill var(--dot-duration, 3000ms) linear forwards;
}

.gallery-dot-fill.is-paused {
  animation-play-state: paused;
}

@keyframes gallery-dot-fill {
  from { width: 0; }
  to { width: 100%; }
}

/* =========================================================
   MOBILE
   ========================================================= */

@media (max-width: 800px) {
  .rotating-grid__group {
    touch-action: pan-y;
    grid-template-columns: repeat(var(--rg-cols-mobile, 2), minmax(0, 1fr));
    gap: 1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rotating-grid__group > * {
    transition-duration: 0.01ms;
  }

  .gallery-dot-fill {
    animation: none;
    width: 100%;
  }
}
</style>
